import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, XCircle, AlertTriangle, Home, User } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const status = params.get('status') || 'unknown';
  const orderId = params.get('order_id') || '';
  const amount = params.get('amount') || '';
  const reason = params.get('reason') || '';
  const navigate = useNavigate();
  const { clearCart } = useCart() || {};

  useEffect(() => {
    if (status === 'success' && clearCart) clearCart();
  }, [status, clearCart]);

  const isSuccess = status === 'success';
  const isCancelled = status === 'cancel';
  const isError = status === 'error';

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4 py-12">
      <Card className="bg-zinc-900/50 border-zinc-800 max-w-md w-full" data-testid="payment-result-card">
        <CardContent className="p-8 text-center">
          {isSuccess ? (
            <>
              <CheckCircle className="mx-auto text-green-500 mb-4" size={72} data-testid="payment-success-icon" />
              <h2 className="text-2xl font-bold text-white mb-2">Paiement reussi !</h2>
              {amount && <p className="text-3xl font-bold text-green-400 mb-2">{amount} EUR</p>}
              <p className="text-zinc-400 mb-2">Votre commande est confirmee.</p>
              {orderId && <p className="text-xs text-zinc-500 mb-6">Reference : {orderId}</p>}
              <p className="text-sm text-zinc-500 mb-6">Un email de confirmation vient de vous etre envoye.</p>
            </>
          ) : isCancelled ? (
            <>
              <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={72} />
              <h2 className="text-2xl font-bold text-white mb-2">Paiement annule</h2>
              <p className="text-zinc-400 mb-6">Vous avez annule la transaction. Aucun debit n'a ete effectue.</p>
            </>
          ) : isError ? (
            <>
              <XCircle className="mx-auto text-red-500 mb-4" size={72} />
              <h2 className="text-2xl font-bold text-white mb-2">Erreur de paiement</h2>
              <p className="text-zinc-400 mb-2">
                {reason === 'signature' ? 'Verification de signature echouee.' : 'Une erreur est survenue lors du paiement.'}
              </p>
              <p className="text-sm text-zinc-500 mb-6">Aucun debit. Merci de reessayer ou contactez-nous.</p>
            </>
          ) : (
            <>
              <XCircle className="mx-auto text-red-500 mb-4" size={72} />
              <h2 className="text-2xl font-bold text-white mb-2">Paiement refuse</h2>
              <p className="text-zinc-400 mb-2">Votre paiement a ete refuse par votre banque.</p>
              {orderId && <p className="text-xs text-zinc-500 mb-6">Reference : {orderId}</p>}
              <p className="text-sm text-zinc-500 mb-6">Verifiez votre carte ou reessayez avec un autre moyen de paiement.</p>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-zinc-700"
              data-testid="payment-result-home"
            >
              <Home size={16} className="mr-2" /> Accueil
            </Button>
            <Button
              onClick={() => navigate('/mon-compte')}
              className="bg-red-600 hover:bg-red-700"
              data-testid="payment-result-account"
            >
              <User size={16} className="mr-2" /> Mon compte
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
